import { GetServerSideProps } from 'next';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { DecisionsPageFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import { getServerPageGetDecisions } from 'api/graphql-helper';

import { ROUTES } from 'constants/routes';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { getXMLSidebarData, IXMLSidebarData } from 'utils/aws-s3-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { AccordionList } from 'modules/accordion-list';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import Layout from 'modules/layout/layout';
import IntroHeader from 'modules/intro-header/intro-header';

export interface IDecisionsPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	decisionsPage?: DecisionsPageFieldsFragment;
	sidebar?: IXMLSidebarData;
	id?: string;
}

const DecisionsPage = ({ pageInformation, decisionsPage, sidebar, id }: IDecisionsPageProps): React.JSX.Element => {
	const { modules, bodyText, sidebarCurrentYearTitle, sidebarArchiveTitle, archiveLink } = { ...decisionsPage };
	const seoModule = decisionsPage?.seo;

	return (
		<Layout
			contentId={id}
			typeName={decisionsPage?.__typename}
			navbar={pageInformation?.navbar}
			footer={pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: decisionsPage?.header?.backgroundColor || 'white',
					padding: 'none'
				}}
			>
				<IntroHeader module={decisionsPage?.header} slug={ROUTES.DECISIONS.fullPath} />
			</BaseModule>

			<BaseModule
				defaultModuleOptions={{
					backgroundColor: decisionsPage?.header?.backgroundColor === 'white' ? 'grey' : 'white',
					padding: 'medium'
				}}
			>
				<AccordionList
					link={archiveLink}
					archiveTitle={sidebarArchiveTitle}
					currentYearTitle={sidebarCurrentYearTitle}
					description={bodyText}
					sidebar={sidebar}
				/>
			</BaseModule>

			<ModuleBuilder modules={modules} slug={ROUTES.DECISIONS.fullPath} />
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetDecisions(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);

		const { pageInformation, decisionsPage } = result.props.data;

		if (!decisionsPage?.data?.attributes) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				decisionsPage: decisionsPage.data?.attributes,
				id: decisionsPage.data?.documentId,
				sidebar: await getXMLSidebarData(),
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'decisions-page',
						contentTypeId: 1
					}
				}
			}
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in besluiten page', error);

		return {
			notFound: true
		};
	}
};

export default DecisionsPage;
